import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { useAssetsVariables } from "../contexts/assets-variables";
import useInventoryFilters, {
  DEFAULT_INVENTORY_FILTER_PARAMS,
} from "../hooks/capture/useInventoryFilters";
import useQueryParams from "../hooks/useQueryParams";
import useAvailableWarehouses from "../hooks/capture/useAvailableWarehouses";
import useAvailableCostCenters from "../hooks/capture/useAvailableCostCenters";

const costCenterLabelFor = costCenter =>
  `${costCenter.costCenterName || ""}${
    costCenter.costCenterCode ? ` ${costCenter.costCenterCode}` : ""
  }`.trim();

export default function InventoryFiltersModal({
  onClose,
  paramKeys = DEFAULT_INVENTORY_FILTER_PARAMS,
  inventoryFromParams = false,
}) {
  const searchParams = useQueryParams();
  const assetsVariables = useAssetsVariables();
  const { value, onChangeInventory, onChangeWarehouse, onChangeCostCenter } = useInventoryFilters({
    variables: assetsVariables?.variables,
    paramKeys,
    inventoryFromParams,
  });
  const warehouses = useAvailableWarehouses();
  const costCenters = useAvailableCostCenters();
  const warehouseId = searchParams.get(paramKeys.warehouseId) || "all";
  const costCenterId = searchParams.get(paramKeys.costCenterId) || "all";

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box position="relative">
          <Typography fontSize={24} fontWeight={500} align="center" color="textPrimary">
            Inventory Filters
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box p={1}>
          <RadioGroup value={value} onChange={onChangeInventory}>
            <FormControlLabel
              sx={{ m: 0 }}
              value="showOnlyInStockItems"
              control={<Radio />}
              label="In Warehouse Only"
            />
            <FormControlLabel
              sx={{ m: 0 }}
              value="showStorePageScope"
              control={<Radio />}
              label="Available Now Only (Matches Store)"
            />
            <FormControlLabel
              sx={{ m: 0 }}
              value="showOutOfWarehouse"
              control={<Radio />}
              label="Out of Warehouse Only"
            />
            <FormControlLabel
              sx={{ m: 0 }}
              value="showAllInventory"
              control={<Radio />}
              label="All Inventory"
            />
          </RadioGroup>
          {warehouses.length > 1 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography fontSize={18} color="textPrimary" sx={{ p: 2 }}>
                Warehouse Filters
              </Typography>
              <RadioGroup value={warehouseId} onChange={onChangeWarehouse}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  value="all"
                  control={<Radio />}
                  label="All Warehouses"
                />
                {warehouses.map(warehouse => (
                  <FormControlLabel
                    key={warehouse.warehouseId}
                    sx={{ m: 0 }}
                    value={warehouse.warehouseId}
                    control={<Radio />}
                    label={warehouse.name}
                  />
                ))}
              </RadioGroup>
            </>
          )}
          {costCenters.length > 1 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography fontSize={18} color="textPrimary" sx={{ p: 2 }}>
                Cost Center Filters
              </Typography>
              <RadioGroup value={costCenterId} onChange={onChangeCostCenter}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  value="all"
                  control={<Radio />}
                  label="All Cost Centers"
                />
                {costCenters.map(costCenter => (
                  <FormControlLabel
                    key={costCenter.id}
                    sx={{ m: 0 }}
                    value={String(costCenter.id)}
                    control={<Radio />}
                    label={costCenterLabelFor(costCenter)}
                  />
                ))}
              </RadioGroup>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
